import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday, parseISO } from "date-fns";
import { Plus, ChevronLeft, ChevronRight, Save, X, Wallet, AlertCircle, RefreshCw, Copy, CopyCheck, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import BankAccountManager from "../components/BankAccountManager";

import LastUpdated from "../components/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BankBalancePage = () => {
  const [accounts, setAccounts] = useState([]);
  const [balances, setBalances] = useState({});
  const [dirtyBalances, setDirtyBalances] = useState({});
  // Timing flags per date: { "date": { includes_received: bool, includes_made: bool } }
  const [timingFlags, setTimingFlags] = useState({});
  const [dirtyTimingFlags, setDirtyTimingFlags] = useState({});
  const [orgDefaults, setOrgDefaults] = useState({ includes_received_payments: true, includes_made_payments: false });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAccountManager, setShowAccountManager] = useState(false);

  // Generate days for current month
  const daysInMonth = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  // Date range strings for API
  const dateRange = useMemo(() => ({
    from: format(startOfMonth(currentMonth), "yyyy-MM-dd"),
    to: format(endOfMonth(currentMonth), "yyyy-MM-dd")
  }), [currentMonth]);

  // Fetch bank accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts`);
      setAccounts(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching accounts:", err);
      setError("Failed to load bank accounts");
      return [];
    }
  }, []);

  // Fetch balances for date range
  const fetchBalances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/balances`, {
        params: { from_date: dateRange.from, to_date: dateRange.to }
      });
      
      // Convert to lookup map: { "accountId_date": balance }
      const balanceMap = {};
      const flagsMap = {};
      response.data.forEach(b => {
        balanceMap[`${b.bank_account_id}_${b.date}`] = b.balance;
        // Store timing flags per date (take first occurrence)
        if (!flagsMap[b.date]) {
          flagsMap[b.date] = {
            includes_received: b.includes_received_payments ?? true,
            includes_made: b.includes_made_payments ?? false
          };
        }
      });
      setBalances(balanceMap);
      setTimingFlags(flagsMap);
    } catch (err) {
      console.error("Error fetching balances:", err);
      setError("Failed to load balances");
    }
  }, [dateRange]);

  // Fetch organization defaults
  const fetchOrgDefaults = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/organization`);
      if (response.data?.bank_balance_defaults) {
        setOrgDefaults(response.data.bank_balance_defaults);
      }
    } catch (err) {
      console.error("Error fetching org defaults:", err);
    }
  }, []);

  useEffect(() => { document.title = "Bank Balance - Floatly"; }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await fetchOrgDefaults();
      await fetchAccounts();
      await fetchBalances();
      setLoading(false);
    };
    loadData();
  }, [fetchAccounts, fetchBalances, fetchOrgDefaults]);

  // Get balance for cell
  const getBalance = (accountId, dateStr) => {
    const key = `${accountId}_${dateStr}`;
    if (dirtyBalances[key] !== undefined) {
      return dirtyBalances[key];
    }
    return balances[key] ?? 0;
  };

  // Get timing flags for a date
  const getTimingFlags = (dateStr) => {
    if (dirtyTimingFlags[dateStr]) {
      return dirtyTimingFlags[dateStr];
    }
    if (timingFlags[dateStr]) {
      return timingFlags[dateStr];
    }
    // Return org defaults
    return {
      includes_received: orgDefaults.includes_received_payments,
      includes_made: orgDefaults.includes_made_payments
    };
  };

  // Handle timing flag change
  const handleTimingFlagChange = (dateStr, flagType, value) => {
    setDirtyTimingFlags(prev => ({
      ...prev,
      [dateStr]: {
        ...getTimingFlags(dateStr),
        [flagType]: value
      }
    }));
  };

  // Handle balance change
  const handleBalanceChange = (accountId, dateStr, value) => {
    const key = `${accountId}_${dateStr}`;
    const numValue = value === "" ? 0 : parseFloat(value);
    
    if (isNaN(numValue)) return;
    if (numValue < -999999.99 || numValue > 999999.99) return;
    
    setDirtyBalances(prev => ({
      ...prev,
      [key]: numValue
    }));
  };

  // Calculate row total
  const getRowTotal = (dateStr) => {
    return accounts.reduce((sum, account) => {
      return sum + getBalance(account.id, dateStr);
    }, 0);
  };

  // Copy balances from previous day
  const copyFromPreviousDay = (currentDateStr, dayIndex) => {
    if (dayIndex === 0 || accounts.length === 0) return;
    
    const prevDateStr = format(daysInMonth[dayIndex - 1], "yyyy-MM-dd");
    const newDirtyBalances = { ...dirtyBalances };
    
    accounts.forEach(account => {
      const prevBalance = getBalance(account.id, prevDateStr);
      const key = `${account.id}_${currentDateStr}`;
      newDirtyBalances[key] = prevBalance;
    });
    
    setDirtyBalances(newDirtyBalances);
    toast.success("Balances copied from previous day");
  };

  // Check if row has same balances as previous day
  const hasSameAsPrevious = (currentDateStr, dayIndex) => {
    if (dayIndex === 0 || accounts.length === 0) return false;
    
    const prevDateStr = format(daysInMonth[dayIndex - 1], "yyyy-MM-dd");
    return accounts.every(account => {
      const currentBalance = getBalance(account.id, currentDateStr);
      const prevBalance = getBalance(account.id, prevDateStr);
      return currentBalance === prevBalance;
    });
  };

  // Count unsaved changes
  const unsavedCount = Object.keys(dirtyBalances).length + Object.keys(dirtyTimingFlags).length;

  // Save all changes
  const handleSave = async () => {
    if (unsavedCount === 0) return;
    
    setSaving(true);
    try {
      // Build balances to update, including timing flags
      const balancesToUpdate = [];
      const processedDates = new Set();
      
      // Process dirty balances
      Object.entries(dirtyBalances).forEach(([key, balance]) => {
        const [accountId, date] = key.split("_");
        const flags = getTimingFlags(date);
        balancesToUpdate.push({ 
          bank_account_id: accountId, 
          date, 
          balance,
          includes_received_payments: flags.includes_received,
          includes_made_payments: flags.includes_made
        });
        processedDates.add(date);
      });
      
      // Process dirty timing flags for dates not yet in balancesToUpdate
      Object.entries(dirtyTimingFlags).forEach(([date, flags]) => {
        if (!processedDates.has(date)) {
          // Need to update all accounts for this date with new flags
          accounts.forEach(account => {
            const balance = getBalance(account.id, date);
            if (balance !== 0 || balances[`${account.id}_${date}`] !== undefined) {
              balancesToUpdate.push({
                bank_account_id: account.id,
                date,
                balance,
                includes_received_payments: flags.includes_received,
                includes_made_payments: flags.includes_made
              });
            }
          });
        }
      });

      if (balancesToUpdate.length > 0) {
        await axios.patch(`${API}/balances/bulk`, { balances: balancesToUpdate });
      }
      
      // Merge dirty balances into main balances
      setBalances(prev => ({ ...prev, ...dirtyBalances }));
      setTimingFlags(prev => ({ ...prev, ...dirtyTimingFlags }));
      setDirtyBalances({});
      setDirtyTimingFlags({});
      toast.success("Changes saved successfully");
    } catch (err) {
      console.error("Error saving balances:", err);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setDirtyBalances({});
    setDirtyTimingFlags({});
    toast.info("Changes discarded");
  };

  // Month navigation
  const goToPrevMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
  const goToNextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));
  const goToToday = () => setCurrentMonth(new Date());

  // Format balance display
  const formatBalance = (value) => {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number for input display (with thousand separators)
  const formatInputDisplay = (value) => {
    if (value === 0 || value === "0" || value === "") return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "0";
    return num.toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  // Parse formatted input back to number
  const parseInputValue = (str) => {
    if (!str || str === "") return 0;
    // Remove thousand separators and replace comma with dot
    const cleaned = str.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  // Handle account created/updated
  const handleAccountChange = () => {
    fetchAccounts();
    fetchBalances();
  };

  // Retry loading
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    fetchAccounts().then(() => fetchBalances()).finally(() => setLoading(false));
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={handleRetry} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="bank-balance-page">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="page-title">
                Floatly
              </h1>
              <p className="text-muted-foreground mt-1">Bank Balance Tracker</p>
              <LastUpdated page="bank-balance" />
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground" data-testid="account-count">
                {accounts.length} account{accounts.length !== 1 ? "s" : ""}
                {unsavedCount > 0 && (
                  <span className="ml-2 text-accent font-medium">
                    • {unsavedCount} unsaved
                  </span>
                )}
              </span>
              <Button 
                onClick={() => setShowAccountManager(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                data-testid="manage-accounts-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Manage Accounts
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Date Navigator */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-4 md:px-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevMonth}
                className="rounded-full"
                data-testid="prev-month-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <h2 className="text-xl md:text-2xl font-semibold min-w-[200px] text-center" data-testid="current-month">
                {format(currentMonth, "MMMM yyyy")}
              </h2>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNextMonth}
                className="rounded-full"
                data-testid="next-month-button"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            <Button
              variant="outline"
              onClick={goToToday}
              className="rounded-full"
              data-testid="today-button"
            >
              Today
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6 md:px-12">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <Wallet className="w-16 h-16 mb-4 text-muted-foreground/50" />
            <h3>No bank accounts yet</h3>
            <p>Add your first bank account to start tracking balances</p>
            <Button 
              onClick={() => setShowAccountManager(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
              data-testid="add-first-account-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Account
            </Button>
          </div>
        ) : (
          <div className="bg-white border rounded-xl shadow-sm" data-testid="balance-table-container">
            <div className="balance-table-wrapper rounded-xl">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white z-10 min-w-[140px]">Date</th>
                    {accounts.map(account => (
                      <th key={account.id} className="min-w-[160px] text-right">
                        <div className="flex flex-col items-end">
                          <span>{account.name}</span>
                          <span className="text-xs font-normal opacity-60">{account.currency}</span>
                        </div>
                      </th>
                    ))}
                    <th className="min-w-[160px] text-right bg-muted/30">Total</th>
                    <TooltipProvider>
                      <th className="min-w-[80px] text-center px-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help text-xs">Incl. rec.</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[200px]">
                            <p className="text-xs">Received payments of this day are already included in the bank balance</p>
                          </TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="min-w-[80px] text-center px-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help text-xs">Incl. paid</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[200px]">
                            <p className="text-xs">Made payments of this day are already included in the bank balance</p>
                          </TooltipContent>
                        </Tooltip>
                      </th>
                    </TooltipProvider>
                  </tr>
                </thead>
                <tbody>
                  {daysInMonth.map((day, dayIndex) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const isTodayRow = isToday(day);
                    const rowTotal = getRowTotal(dateStr);
                    const isSameAsPrev = hasSameAsPrevious(dateStr, dayIndex);
                    
                    return (
                      <tr 
                        key={dateStr} 
                        className={isTodayRow ? "today-row" : "hover:bg-muted/30"}
                        data-testid={`row-${dateStr}`}
                      >
                        <td className="sticky left-0 bg-inherit z-10">
                          <div className="date-cell flex items-center gap-2">
                            <span className={isTodayRow ? "font-bold" : ""}>
                              {format(day, "EEE, MMM d")}
                            </span>
                            {isTodayRow && (
                              <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                                Today
                              </span>
                            )}
                            {dayIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyFromPreviousDay(dateStr, dayIndex)}
                                className={`h-6 w-6 ml-auto ${isSameAsPrev ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                                title={isSameAsPrev ? "Same as previous day" : "Copy from previous day"}
                                data-testid={`copy-prev-${dateStr}`}
                              >
                                {isSameAsPrev ? (
                                  <CopyCheck className="w-3.5 h-3.5" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                        
                        {accounts.map(account => {
                          const balance = getBalance(account.id, dateStr);
                          const isDirty = dirtyBalances[`${account.id}_${dateStr}`] !== undefined;
                          
                          return (
                            <td key={account.id} className={isDirty ? "bg-accent/5" : ""}>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={formatInputDisplay(balance)}
                                onChange={(e) => {
                                  const parsed = parseInputValue(e.target.value);
                                  handleBalanceChange(account.id, dateStr, parsed);
                                }}
                                onFocus={(e) => {
                                  // Show raw number for editing
                                  e.target.value = balance !== 0 ? balance.toString().replace(".", ",") : "";
                                  e.target.select();
                                }}
                                onBlur={(e) => {
                                  // Re-format on blur
                                  const parsed = parseInputValue(e.target.value);
                                  e.target.value = formatInputDisplay(parsed);
                                }}
                                className={`balance-input ${balance < 0 ? "balance-negative" : ""}`}
                                data-testid={`balance-input-${account.id}-${dateStr}`}
                              />
                            </td>
                          );
                        })}
                        
                        <td className="bg-muted/30">
                          <div className={`total-cell ${rowTotal < 0 ? "balance-negative" : "balance-positive"}`} data-testid={`total-${dateStr}`}>
                            {formatBalance(rowTotal)}
                          </div>
                        </td>
                        
                        {/* Timing flags checkboxes */}
                        {(() => {
                          const flags = getTimingFlags(dateStr);
                          const hasBalance = rowTotal !== 0 || Object.keys(balances).some(k => k.endsWith(`_${dateStr}`));
                          const isDirtyFlag = dirtyTimingFlags[dateStr] !== undefined;
                          
                          return (
                            <>
                              <td className={`text-center ${isDirtyFlag ? "bg-accent/5" : ""}`}>
                                {hasBalance && (
                                  <Checkbox
                                    checked={flags.includes_received}
                                    onCheckedChange={(checked) => handleTimingFlagChange(dateStr, "includes_received", checked)}
                                    data-testid={`flag-received-${dateStr}`}
                                    className="mx-auto"
                                  />
                                )}
                              </td>
                              <td className={`text-center ${isDirtyFlag ? "bg-accent/5" : ""}`}>
                                {hasBalance && (
                                  <Checkbox
                                    checked={flags.includes_made}
                                    onCheckedChange={(checked) => handleTimingFlagChange(dateStr, "includes_made", checked)}
                                    data-testid={`flag-made-${dateStr}`}
                                    className="mx-auto"
                                  />
                                )}
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Floating Action Bar */}
      {unsavedCount > 0 && (
        <div className="action-bar" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            {unsavedCount} unsaved change{unsavedCount !== 1 ? "s" : ""}
          </span>
          <Button
            variant="outline"
            onClick={handleDiscard}
            className="rounded-full"
            data-testid="discard-button"
          >
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
            data-testid="save-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}

      {/* Account Manager Modal */}
      <BankAccountManager
        open={showAccountManager}
        onOpenChange={setShowAccountManager}
        accounts={accounts}
        onAccountChange={handleAccountChange}
      />
    </div>
  );
};

export default BankBalancePage;
